import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { UserAsset } from '@models/dashboard.model';
import { ShoppingCartData, SimOptions } from '@models/shopping-cart.model';
import {
  ACTIVITY_3_ASSET_CARD_FIELDS,
  FieldErrorMessage,
  SimFormOutputEvent
} from '@models/shopping.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { TranslationService } from '@services/translation/translation.service';
import { Subscription } from 'rxjs';
import { DataLoaderDirective, LoadingSpinnerComponent } from 'tangoe-design-system';

import { AssetSelectionItemComponent } from '../asset/selection-item/asset-selection-item.component';

@Component({
  selector: 'ts-device-to-activate',
  templateUrl: './device-to-activate.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslationPipe,
    DataLoaderDirective,
    AssetSelectionItemComponent,
    LoadingSpinnerComponent
  ]
})
export class DeviceToActivateComponent implements OnInit, OnChanges, OnDestroy {
  @Input() existing_asset: UserAsset;
  @Input() shopping_cart: ShoppingCartData;
  @Input() is_review_or_summary: boolean = false;
  @Input() card_wrap: boolean = true;
  @Input() sim_summary_details: SimOptions | undefined;
  @Input() asset_fields: string[] = ACTIVITY_3_ASSET_CARD_FIELDS;
  @Input() field_error_messages: FieldErrorMessage[] = [];
  @Output() valid_sim_form: EventEmitter<SimFormOutputEvent> = new EventEmitter();
  sim_form: FormGroup;
  sim_type: string;
  sim_option_labels: string[];
  private subscriptions: Subscription = new Subscription();

  constructor(private translation_svc: TranslationService) {
    this.sim_option_labels = this.translation_svc.getTranslatedSimOptionLabels();
  }

  ngOnInit(): void {
    this.sim_form = new FormGroup({
      sim_options: new FormControl('', Validators.required),
      sim_input: new FormControl('', [Validators.required])
    });

    if (this.sim_form && this.shopping_cart) this.setFormValue(this.shopping_cart);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.sim_form || !changes['field_error_messages']) return;

    const sim_type: string = this.sim_form.controls['sim_options'].value;
    const errors: Map<string, string> = new Map<string, string>();

    changes['field_error_messages'].currentValue.forEach((error: FieldErrorMessage) => {
      if (!error.error_message.length) return;

      let field_name: string | undefined = undefined;
      switch (error.field_id) {
        case 'new_sim':
          field_name = 'own';
          break;
        case 'new_eid':
          field_name = 'esim';
          break;
        default:
          break;
      }
      if (field_name) errors.set(field_name, error.error_message.join('\n'));
    });

    this.sim_form.controls['sim_input'].setErrors(
      errors.has(sim_type)
        ? {
            invalid: errors.get(sim_type)
          }
        : null
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get loadAsset(): string {
    if (!this.existing_asset) return 'loading';
    else return 'assets';
  }

  simOptionAvailable(sim_option: string): boolean {
    if (!this.existing_asset.device.tech_specs?.sim_compatibility) return true;

    switch (sim_option) {
      case 'own':
      case 'new':
        return ['Either SIM Or eSIM', 'SIM'].includes(
          this.existing_asset.device.tech_specs.sim_compatibility!
        )
          ? true
          : false;
      case 'esim':
        return ['Either SIM Or eSIM', 'eSIM Only'].includes(
          this.existing_asset.device.tech_specs.sim_compatibility!
        )
          ? true
          : false;
      default:
        return false;
    }
  }

  selectSimOption(event: any): void {
    this.sim_form.controls['sim_input'].reset();
    this.sim_type = event.target.defaultValue;

    if (event.target.checked && ['new'].includes(this.sim_type)) {
      this.valid_sim_form.emit({
        valid: true,
        type: this.sim_type,
        value: this.sim_form.controls['sim_input'].value ?? undefined,
        identifier: this.existing_asset?.device?.imei ?? undefined
      });
    } else if (this.sim_type === 'esim' && this.existing_asset?.device?.eid) {
      this.sim_form.controls['sim_input'].setValidators([
        Validators.required,
        Validators.minLength(32)
      ]);
      this.sim_form.controls['sim_input'].setValue(this.existing_asset?.device?.eid);
      this.sim_form.controls['sim_input'].disable();
      this.valid_sim_form.emit({
        valid: true,
        type: this.sim_type,
        value: this.sim_form.controls['sim_input'].value ?? undefined,
        identifier: this.existing_asset?.device?.imei ?? undefined
      });
    } else {
      this.sim_form.controls['sim_input'].setValue(null);
      this.sim_form.controls['sim_input'].enable();

      if (this.sim_type === 'esim')
        this.sim_form.controls['sim_input'].setValidators([
          Validators.required,
          Validators.minLength(32)
        ]);
      else this.sim_form.controls['sim_input'].setValidators([Validators.required]);

      this.sim_form.updateValueAndValidity();

      this.valid_sim_form.emit({
        valid: false,
        type: this.sim_type,
        value: this.sim_form.controls['sim_input'].value ?? undefined,
        identifier: this.existing_asset?.device?.imei ?? undefined
      });
    }
  }

  simNumberInput(event: any): void {
    this.valid_sim_form.emit({
      valid:
        event.target.value && this.sim_form.valid && !this.simInputValidationClasses(this.sim_type)
          ? true
          : false,
      type: this.sim_type,
      value: this.sim_form.controls['sim_input'].value ?? undefined,
      identifier: this.existing_asset.device.imei! ?? undefined
    });
  }

  simInputValidationClasses(input_type: string): string {
    let classes: string = '';
    let control: AbstractControl = this.sim_form.controls['sim_input'];

    if (control.dirty && [null, ''].includes(control.value)) classes = 'is-invalid';
    else if (control.touched && control.value) {
      if (input_type === 'esim' && control.value.length !== 32) {
        classes = 'is-invalid';
      }
    }

    return classes;
  }

  private setFormValue(shopping_cart: ShoppingCartData): void {
    this.sim_form.controls['sim_options'].setValue(
      !shopping_cart?.order_sim && shopping_cart?.new_sim
        ? 'own'
        : shopping_cart?.esim_activation && shopping_cart?.new_eid
          ? 'esim'
          : shopping_cart?.order_sim
            ? 'new'
            : ''
    );
    this.sim_type = this.sim_form.controls['sim_options'].value;

    if (this.sim_type === 'esim')
      this.sim_form.controls['sim_input'].setValidators([
        Validators.required,
        Validators.minLength(32)
      ]);

    this.sim_form.controls['sim_input'].setValue(
      !shopping_cart?.order_sim && shopping_cart?.new_sim
        ? shopping_cart.new_sim
        : shopping_cart?.esim_activation && shopping_cart?.new_eid
          ? shopping_cart.new_eid
          : ''
    );

    if (
      this.existing_asset &&
      this.existing_asset.device.eid &&
      this.sim_type === 'esim' &&
      this.sim_form.controls['sim_input'].value
    ) {
      this.sim_form.controls['sim_input'].disable();
    }

    this.valid_sim_form.emit({
      valid: this.sim_form.controls['sim_options'].value && this.sim_form?.valid ? true : false,
      type: this.sim_form.controls['sim_options'].value,
      value: this.sim_form.controls['sim_input'].value ?? undefined,
      identifier: this.existing_asset.device.imei ?? undefined
    });
  }
}
